import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { gql, useQuery, useMutation } from '@apollo/client';
import { ChevronRight, X } from 'lucide-react';
import apolloClient from '../../../lib/apolloClient';
import Button from '../../../../../ui/canon/Button';
import ReadinessHtmlBlocks from '../../Succession/ReadinessHtmlBlocks';
import ReadinessRing from '../../Succession/ReadinessRing';

// Loads the assessment plan template (questions), the user's progress so far
// (for resume), and the company's continuity word for copy interpolation.
const GET_ASSESSMENT = gql`
  query GetReadinessAssessment($slug: String!) {
    getCurrentUser {
      id
      retirement_timeline_bucket
      company {
        id
        catastrophic_planning_word
      }
    }
    readinessAssessmentApplicableCategories
    readinessAssessmentInterstitials(slug: $slug) {
      key
      planQuestionId
      placement
      sectionTitle
      blocks
    }
    planTemplate(slug: $slug) {
      id
      slug
      planModules {
        id
        sortOrder
        planSections {
          id
          title
          sortOrder
          planQuestions {
            id
            questionText
            helpText
            category
            metadata
            sortOrder
          }
        }
      }
    }
  }
`;

const GET_ASSESSMENT_PROGRESS = gql`
  query GetReadinessAssessmentProgress($planTemplateId: ID!) {
    userPlanProgress(planTemplateId: $planTemplateId) {
      id
      status
      userPlanResponses {
        id
        planQuestionId
      }
    }
  }
`;

const ANSWER_PLAN_QUESTION = gql`
  mutation AnswerReadinessQuestion($planTemplateId: ID!, $planQuestionId: ID!, $answer: JSON!) {
    answerPlanQuestion(planTemplateId: $planTemplateId, planQuestionId: $planQuestionId, answer: $answer) {
      userPlanProgress {
        id
        status
      }
      errors
    }
  }
`;

const SET_TIMELINE = gql`
  mutation SetReadinessTimeline($planTemplateId: ID!, $bucket: String!) {
    setReadinessAssessmentTimeline(planTemplateId: $planTemplateId, bucket: $bucket) {
      user {
        id
        retirement_timeline_bucket
      }
      applicableCategories
      errors
    }
  }
`;

const GET_RESULT = gql`
  query GetReadinessAssessmentResult {
    readinessAssessmentResult {
      id
      categoryScores
      categoryStatuses {
        category
        status
        inScope
      }
      overallScore
      completed
      suggestedActivityIds
      lowScoreCategories
    }
  }
`;

const CATEGORY_LABELS = {
  valuation: 'Valuation',
  catastrophic_plan: 'Catastrophic Planning',
  partnering: 'Partnering & Succession',
  deal_structure: 'Deal Structure',
  communication: 'Client Communication',
  emotional_readiness: 'Emotional Readiness',
};

// CATEGORY_BLURBS removed with the old results screen. It was per-gap prose
// marked "wording subject to RJ/ICD verification" — i.e. never approved — and it
// restated what the advisor had just read on each question's "Why this matters"
// interstitial. The closing screen names the gaps instead and points at the
// further reading attached to them on the tile.

const interpolate = (text, continuityWord) =>
  (text || '').replace(/%\{continuity_word\}/g, continuityWord || 'Catastrophic Planning');

// The retirement horizon opens the assessment, exactly as it opened the legacy
// Paddy version — and it decides how many questions follow. Buckets and copy
// match ModernizedMotivationOnboarding/StepTimeline so an advisor sees the same
// question they answered at onboarding, prefilled with their answer.
const TIMELINE_OPTIONS = [
  { key: 'one_to_three', years: '1-3', label: 'Near Term', description: 'Planning and preparation' },
  { key: 'four_to_six', years: '4-6', label: 'Mid Term', description: 'Strategic positioning' },
  { key: 'seven_plus', years: '7+', label: 'Long Term', description: 'Flexibility and growth' },
];

const TimelineStep = ({ value, onChange }) => (
  <div>
    <p className="text-succession-text-primary font-medium text-base leading-snug mb-1">
      When do you plan to retire?
    </p>
    <p className="text-xs text-succession-text-secondary mb-5">
      This tailors the rest of the questions to your time horizon.
    </p>

    {/* One row per option rather than three columns. Nothing wraps or truncates,
        so copy length stops mattering — the labels are not final and RJ may
        lengthen them.

        No `block` class and no `text-[Npx]` anywhere here. Legacy
        application.css defines `.block { padding: 25px 0 }`, which was adding
        ~150px of dead space per card, and the succession layout loads Tailwind
        v2 from CDN, which predates JIT — so `text-[11px]` generated no CSS and
        the description inherited 16px, rendering LARGER than the label above it. */}
    <div className="flex flex-col gap-2" role="radiogroup" aria-label="Years until retirement">
      {TIMELINE_OPTIONS.map((opt) => {
        const selected = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.key)}
            className={`flex w-full items-center gap-4 px-4 py-3 rounded-lg border text-left transition-all ${
              selected
                ? 'border-succession-primary bg-succession-bg-secondary'
                : 'border-succession-border bg-succession-bg-primary hover:border-succession-primary'
            }`}>
            {/* Each option carries its own unit. The old "YEARS UNTIL
                RETIREMENT" caption sat below the choices and rendered at 16px
                because of the same no-op, so it is stated here instead. */}
            <span className="flex-shrink-0 w-16 whitespace-nowrap text-base font-semibold text-succession-text-primary">
              {opt.years}
              <span className="ml-1 text-xs font-medium text-succession-text-secondary">yrs</span>
            </span>
            <span className="flex flex-1 flex-col min-w-0">
              <span className="text-sm font-semibold text-succession-text-primary">{opt.label}</span>
              <span className="text-xs text-succession-text-secondary leading-snug">{opt.description}</span>
            </span>
            <span
              className={`flex-shrink-0 flex items-center justify-center w-4 h-4 rounded-full border-2 transition-colors ${
                selected ? 'border-succession-primary' : 'border-succession-border'}`}
              aria-hidden>
              {selected && <span className="w-2 h-2 rounded-full bg-succession-primary" />}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

TimelineStep.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

TimelineStep.defaultProps = { value: null };

// Flatten modules -> sections -> questions in sort order.
const flattenQuestions = (planTemplate) => {
  if (!planTemplate) return [];
  const modules = [...(planTemplate.planModules || [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const out = [];
  modules.forEach((mod) => {
    const sections = [...(mod.planSections || [])].sort((a, b) => a.sortOrder - b.sortOrder);
    sections.forEach((section) => {
      const questions = [...(section.planQuestions || [])].sort((a, b) => a.sortOrder - b.sortOrder);
      questions.forEach((q) => out.push({ ...q, sectionTitle: section.title }));
    });
  });
  return out;
};

/**
 * ReadinessAssessmentFlow
 *
 * Data-driven succession readiness assessment. Questions come from a PlanBuilder
 * template (queried by slug); each answer is persisted through the standard
 * answerPlanQuestion mutation. When the final answer flips the plan to
 * `completed`, the backend scores it (UserPlanProgress after_update) and we show
 * a per-category results summary.
 *
 * Props:
 * - planSlug: PlanBuilder template slug ('succession-readiness-assessment' | '...-rj')
 * - onComplete: (result) => void  — called from the results screen CTA
 * - onClose: () => void           — called when the user closes without completing
 */
const ReadinessAssessmentFlow = ({ planSlug, onComplete, onClose }) => {
  const { data: assessmentData, loading: loadingAssessment } = useQuery(GET_ASSESSMENT, {
    variables: { slug: planSlug },
    fetchPolicy: 'cache-and-network',
    client: apolloClient,
  });

  const planTemplate = assessmentData?.planTemplate || null;
  const planTemplateId = planTemplate?.id;
  const continuityWord = assessmentData?.getCurrentUser?.company?.catastrophic_planning_word;

  // Which questions this advisor is actually asked, per their retirement
  // horizon. The server owns the gating table (ReadinessAssessmentQuestionScope);
  // here we just filter the template by the categories it returns.
  const [applicableCategories, setApplicableCategories] = useState(null);
  const serverCategories = assessmentData?.readinessAssessmentApplicableCategories;
  const categories = applicableCategories || serverCategories;

  const allQuestions = useMemo(() => flattenQuestions(planTemplate), [planTemplate]);
  const questions = useMemo(() => {
    if (!categories) return allQuestions;
    const allowed = new Set(categories);
    return allQuestions.filter((q) => {
      const category = q.metadata?.category || q.category;
      // A question with no category isn't in the gating table — never hide it.
      return !category || allowed.has(category);
    });
  }, [allQuestions, categories]);

  const { data: progressData, loading: loadingProgress } = useQuery(GET_ASSESSMENT_PROGRESS, {
    variables: { planTemplateId },
    skip: !planTemplateId,
    fetchPolicy: 'network-only',
    client: apolloClient,
  });

  const [answerQuestion, { loading: answering }] = useMutation(ANSWER_PLAN_QUESTION, { client: apolloClient });
  const [saveTimeline] = useMutation(SET_TIMELINE, { client: apolloClient });

  const [currentStep, setCurrentStep] = useState(null); // resolved from resume point
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingResult, setLoadingResult] = useState(false);
  // The educational panel currently standing in for the question view, if any:
  // { interstitial, answer, next } where `next` is the step to advance to on
  // Continue. Null means we're showing the question.
  const [panel, setPanel] = useState(null);
  // Retirement horizon, step 1. Seeded from the profile so an advisor who
  // answered at onboarding sees their own answer already selected.
  const [timeline, setTimeline] = useState(null);
  const [timelineDone, setTimelineDone] = useState(false);
  const [savingTimeline, setSavingTimeline] = useState(false);
  const storedTimeline = assessmentData?.getCurrentUser?.retirement_timeline_bucket || null;
  const selectedTimeline = timeline || storedTimeline;

  // Legacy Paddy educational panels, indexed for O(1) lookup on each transition.
  const interstitials = useMemo(
    () => assessmentData?.readinessAssessmentInterstitials || [],
    [assessmentData]
  );
  // Every panel follows a question one-to-one. There was also a question-less
  // "standalone" placement, anchored before a question rather than after one,
  // for the legacy valuation segment; RJ's documents ask valuation directly, so
  // the server no longer emits it.
  const panelsByQuestionId = useMemo(() => {
    const map = {};
    interstitials.forEach((i) => {
      if (i.planQuestionId) map[String(i.planQuestionId)] = i;
    });
    return map;
  }, [interstitials]);

  // Resolve the resume position once both queries have returned: first question
  // with no saved response. Derived via a memo + lazy initialization of state.
  const answeredIds = useMemo(() => {
    const responses = progressData?.userPlanProgress?.userPlanResponses || [];
    return new Set(responses.map((r) => String(r.planQuestionId)));
  }, [progressData]);

  const resolvedStep = useMemo(() => {
    if (!questions.length) return 0;
    const idx = questions.findIndex((q) => !answeredIds.has(String(q.id)));
    return idx === -1 ? questions.length - 1 : idx;
  }, [questions, answeredIds]);

  const step = currentStep === null ? resolvedStep : currentStep;
  const question = questions[step];
  const totalSteps = questions.length;
  const isLastStep = step === totalSteps - 1;

  // The timeline is step 1, so the counter runs over questions + 1. Anyone who
  // hasn't finished the assessment is asked it — preselected from their profile,
  // never skipped. Their horizon may well have moved since onboarding, and it
  // decides which questions they get, so it is not safe to assume.
  const showTimeline = !timelineDone && !completed;
  const displayTotal = totalSteps + 1;
  const displayStep = showTimeline ? 1 : step + 2;

  let footerHint = '';
  if (answering) footerHint = 'Saving…';
  else if (isLastStep) footerHint = 'Last question';

  const loadResult = async () => {
    setLoadingResult(true);
    try {
      const { data } = await apolloClient.query({ query: GET_RESULT, fetchPolicy: 'network-only' });
      setResult(data?.readinessAssessmentResult || null);
    } catch (err) {
      console.error('Failed to load readiness assessment result:', err);
    } finally {
      setLoadingResult(false);
    }
  };

  // Persist the horizon and adopt the question set it implies. The mutation
  // also records the gated-out questions as not-applicable server-side, which
  // is what lets the plan reach `completed` without them.
  const handleTimelineContinue = async () => {
    if (!selectedTimeline || savingTimeline) return;
    setSavingTimeline(true);
    try {
      const { data } = await saveTimeline({
        variables: { planTemplateId, bucket: selectedTimeline },
      });
      const payload = data?.setReadinessAssessmentTimeline;
      const errors = payload?.errors || [];
      if (errors.length > 0) {
        console.error('setReadinessAssessmentTimeline errors:', errors);
        return;
      }
      try {
        if (window.ahoy) window.ahoy.track('readiness_assessment_timeline_set', { bucket: selectedTimeline });
      } catch (_) { /* ahoy optional */ }
      if (payload?.applicableCategories) setApplicableCategories(payload.applicableCategories);
      // The gated question set just changed underneath us. Clearing the index
      // hands control back to the resume memo, which lands on the first
      // unanswered question *within the new set* — question one for a fresh
      // start or a retake, and where they left off for a part-finished pass.
      setCurrentStep(null);
      setTimelineDone(true);
    } catch (err) {
      console.error('Failed to save retirement timeline:', err);
    } finally {
      setSavingTimeline(false);
    }
  };

  const trackPanel = (key, answer) => {
    try {
      if (window.ahoy) window.ahoy.track('readiness_assessment_interstitial_viewed', { key, answer });
    } catch (_) { /* ahoy optional */ }
  };

  const advanceTo = (next) => {
    setPanel(null);
    setCurrentStep(next);
  };

  const handleAnswer = async (value) => {
    if (!question || answering) return;
    try {
      const { data } = await answerQuestion({
        variables: {
          planTemplateId,
          planQuestionId: question.id,
          answer: { value },
        },
      });
      const errors = data?.answerPlanQuestion?.errors || [];
      if (errors.length > 0) {
        console.error('answerPlanQuestion errors:', errors);
        return;
      }
      const after = panelsByQuestionId[String(question.id)];
      const status = data?.answerPlanQuestion?.userPlanProgress?.status;

      if (status === 'completed') {
        // `readiness_assessment_completed` is emitted server-side from
        // UserPlanProgress#score_readiness_assessment. Tracking it here too
        // lost the event whenever the tab closed between the mutation
        // resolving and this line, leaving a scored result with no completion.
        setCompleted(true);
        // The final question earns its educational panel too. `next: null`
        // means Continue falls through to the closing panel / results rather
        // than to another question.
        if (after) {
          trackPanel(after.key, value);
          setPanel({ interstitial: after, answer: value, next: null });
        }
        await loadResult();
        return;
      }

      // Advance only after the mutation resolves (prevents duplicate-progress races).
      const next = Math.min(step + 1, totalSteps - 1);
      if (after) {
        trackPanel(after.key, value);
        setPanel({ interstitial: after, answer: value, next });
        return;
      }
      advanceTo(next);
    } catch (err) {
      console.error('Failed to submit readiness answer:', err);
    }
  };

  // Continue from an educational panel to the question it precedes.
  const handlePanelContinue = () => {
    if (!panel) return;
    // next === null: this was the last question's panel — fall through to the
    // closing panel / results.
    if (panel.next === null) {
      setPanel(null);
      return;
    }
    advanceTo(panel.next);
  };

  const handleBack = () => {
    // Back out of a panel to the question that produced it. The answer is
    // already persisted; answerPlanQuestion upserts, so re-answering is safe.
    if (panel) {
      setPanel(null);
      return;
    }
    if (step > 0) {
      setCurrentStep(step - 1);
      return;
    }
    // Back from the first question returns to the timeline, so an advisor can
    // correct a horizon that produced the wrong question set.
    setTimelineDone(false);
  };

  const loading = loadingAssessment || loadingProgress;
  // Rows for the closing screen, derived exactly as the LaunchPad tile derives
  // them so the ring and the named gaps agree with what the advisor sees next.
  // A category with max 0 was gated out by their horizon and was never asked.
  const statusByCategory = (result?.categoryStatuses || []).reduce((acc, entry) => {
    if (entry && entry.category) acc[entry.category] = entry;
    return acc;
  }, {});
  const resultRows = Object.entries(result?.categoryScores || {})
    .filter(([, sm]) => Number(sm.max) > 0)
    .map(([key, sm]) => ({
      key,
      // Same server-resolved status the LaunchPad tile uses, falling back to the
      // score exactly as this screen did before, so the closing ring can never
      // contradict the tile the advisor lands on next.
      status: statusByCategory[key]?.status
        || (Number(sm.score) < Number(sm.max) ? 'to_do' : 'done'),
    }));
  const gapRows = resultRows.filter((row) => row.status === 'to_do');

  // Belt-and-braces with the server-side prompt gate: if the template isn't
  // available, don't render a broken modal.
  if (!loading && !planTemplate) {
    onClose?.();
    return null;
  }

  const progressPercent = displayTotal ? Math.round((displayStep / displayTotal) * 100) : 0;

  // Portal to <body> so the fixed backdrop covers the true viewport. The
  // Succession LaunchPad has transformed ancestors that would otherwise trap a
  // `fixed` overlay (making inset-0 resolve against the ancestor, not the page),
  // which is why the dim wasn't showing.
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }}>
      <div className="relative w-full max-w-xl bg-succession-bg-primary rounded-2xl shadow-succession-lg border border-succession-border overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-succession-border">
          <div className="flex-1">
            {/* `m-0 border-0 pb-0` is a reset, not styling: application.css
                styles bare h2 with `border-bottom: 1px solid; padding-bottom:
                15px`, which drew an unasked-for rule under the modal title and
                pushed the progress bar down. */}
            <h2 className="m-0 border-0 pb-0 text-base font-semibold text-succession-text-primary">
              Succession Readiness Check
            </h2>
            {/* Interstitials are educational panels BETWEEN steps, so they
                neither increment nor display the question counter. */}
            {!completed && !panel && totalSteps > 0 && (
              <p className="text-xs text-succession-text-secondary mt-0.5">
                Step {displayStep} of {displayTotal}
              </p>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-succession-bg-tertiary transition-colors"
              aria-label="Close assessment">
              <X className="w-4 h-4 text-succession-text-secondary" />
            </button>
          )}
        </div>

        {/* Progress bar */}
        {!completed && (
          <div className="h-1 bg-succession-bg-secondary">
            <div
              className="h-full bg-succession-primary transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}

        {/* Body */}
        {loading && (
          <div className="px-6 py-10 text-center text-sm text-succession-text-secondary">
            Loading…
          </div>
        )}

        {/* Step 1 — retirement horizon. Decides how many questions follow. */}
        {!loading && !panel && showTimeline && (
          <>
            <div className="px-6 py-6">
              <TimelineStep value={selectedTimeline} onChange={setTimeline} />
            </div>
            <div className="flex items-center justify-end px-6 py-4 border-t border-succession-border">
              <Button onClick={handleTimelineContinue} disabled={!selectedTimeline || savingTimeline} className="gap-2">
                {savingTimeline ? 'Saving…' : 'Continue'}
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}

        {/* Educational interstitial — the legacy Paddy post-answer follow-up.
            Stands in for the question view; the answer is already saved. */}
        {!loading && panel && (
          <>
            <div className="px-6 py-6">
              {panel.interstitial.sectionTitle && (
                <p className="text-xs font-medium uppercase tracking-wide text-succession-text-secondary mb-3">
                  {panel.interstitial.sectionTitle}
                </p>
              )}

              {/* Presented as a pull quote so RJ's educational copy reads as
                  guidance rather than body text between two questions.
                  `border-l-4` is on the standard Tailwind scale — arbitrary
                  values like border-l-[3px] are a no-op here, since the
                  succession layout loads Tailwind v2 from CDN.

                  The decorative opening-quote glyph that used to sit above the
                  heading was removed at RJ's request — they read it as a stray
                  character rather than as framing. The left rule carries the
                  pull-quote treatment on its own, so nothing inside this block
                  is absolutely positioned any more.

                  "Why this matters" is not a new string to approve: it is the
                  name of the i18n key this copy comes from
                  (paddy.succession_readiness.why_it_matters.*). */}
              <div className="border-l-4 border-succession-primary pl-5">
                <p className="m-0 mb-2 text-sm font-bold text-succession-text-primary">Why this matters</p>
                {/* Copy promoted from secondary to primary — the whole point of
                    the panel is that this is worth reading. The system-credit
                    sentence rides along as a second block inside the same quote. */}
                <ReadinessHtmlBlocks
                  blocks={panel.interstitial.blocks}
                  textClassName="text-sm text-succession-text-primary leading-relaxed"
                />
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-succession-border">
              <button
                type="button"
                onClick={handleBack}
                className="text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors">
                Back
              </button>
              <Button onClick={handlePanelContinue} className="gap-2">
                Continue
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}

        {!loading && !completed && !panel && !showTimeline && question && (
          <>
            <div className="px-6 py-6">
              <p className="text-succession-text-primary font-medium text-base leading-snug mb-2">
                {interpolate(question.questionText, continuityWord)}
              </p>

              {/* The per-question "Learn more" disclosure is hidden for now, per
                  RJ feedback: the education now lands on the interstitial after
                  the answer ("Why it matters") rather than competing with the
                  question before it. `helpText` is still seeded on every question
                  and still queried, so restoring this is a UI change only. */}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleAnswer('yes')}
                  disabled={answering}
                  className="flex-1 px-4 py-3 rounded-lg border border-succession-border bg-succession-bg-primary text-succession-text-primary text-sm font-medium hover:border-succession-primary hover:bg-succession-bg-secondary transition-all disabled:opacity-50">
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => handleAnswer('no')}
                  disabled={answering}
                  className="flex-1 px-4 py-3 rounded-lg border border-succession-border bg-succession-bg-primary text-succession-text-primary text-sm font-medium hover:border-succession-primary hover:bg-succession-bg-secondary transition-all disabled:opacity-50">
                  No
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-succession-border">
              <button
                type="button"
                onClick={handleBack}
                disabled={answering}
                className="text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors disabled:opacity-30">
                Back
              </button>
              <span className="text-xs text-succession-text-secondary">
                {footerHint}
              </span>
            </div>
          </>
        )}

        {/* One closing screen, not two.

            It used to be the legacy `thank_you` panel followed by a separate
            results screen. That panel was Paddy chat copy describing features
            that no longer exist — a "Succession Readiness To-Do List" on the
            dashboard (the ReadinessActivity mechanism, dropped in the migration
            and replaced by QuickStart personalization) and a "Close Assessment"
            button that lives in PaddyChatDrawer. It also reported a score out of
            a possible N, the same score/max sum removed from the tile because
            `max` mixes answered questions with server-side signals.

            What replaces it says only things that are true and checkable, and
            leads with the finding — which areas came up short — because that is
            the one thing the advisor does not already know. */}
        {completed && !panel && (
          <div className="px-6 py-6">
            {loadingResult && (
              <p className="text-sm text-succession-text-secondary text-center py-6">Scoring your answers…</p>
            )}

            {!loadingResult && result && (
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6">
                {/* Same mark as the LaunchPad tile, so the advisor recognises it
                    when the modal closes behind them. */}
                <ReadinessRing rows={resultRows} overall={Math.round(result.overallScore || 0)} />

                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <p className="m-0 text-base font-semibold text-succession-text-primary">
                    {gapRows.length === 0
                      ? "You're on track across every area"
                      : `${gapRows.length} ${gapRows.length === 1 ? 'area needs' : 'areas need'} attention`}
                  </p>
                  <p className="mt-1 text-sm text-succession-text-secondary">
                    {gapRows.length === 0
                      ? 'Nothing outstanding from this assessment.'
                      : 'Everything else is in good shape.'}
                  </p>

                  {gapRows.length > 0 && (
                    <div className="flex flex-wrap justify-center sm:justify-start gap-1.5 mt-3">
                      {gapRows.map((row) => (
                        <span
                          key={row.key}
                          className="text-xs font-semibold px-2 py-1 rounded-full bg-succession-warning/10 text-succession-warning">
                          {CATEGORY_LABELS[row.key] || row.key}
                        </span>
                      ))}
                    </div>
                  )}

                  <ul className="list-none m-0 p-0 mt-4 space-y-2.5 text-left">
                    <li className="flex items-start gap-2.5">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-succession-bg-secondary text-succession-primary text-xs font-bold flex items-center justify-center mt-0.5">1</span>
                      {/* flex-col, not `block` on the children: legacy
                          application.css defines `.block { padding: 25px 0 }`.
                          Without the column the heading and its description flow
                          inline and run straight into each other. */}
                      <span className="flex min-w-0 flex-col">
                        <span className="text-sm font-semibold text-succession-text-primary">
                          {gapRows.length === 0 ? 'Your next steps are ready' : 'Your next steps now lead with these'}
                        </span>
                        <span className="mt-0.5 text-xs text-succession-text-secondary leading-snug">
                          The guide on your LaunchPad has been reordered around your answers.
                        </span>
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-succession-bg-secondary text-succession-primary text-xs font-bold flex items-center justify-center mt-0.5">2</span>
                      {/* flex-col, not `block` on the children: legacy
                          application.css defines `.block { padding: 25px 0 }`.
                          Without the column the heading and its description flow
                          inline and run straight into each other. */}
                      <span className="flex min-w-0 flex-col">
                        <span className="text-sm font-semibold text-succession-text-primary">
                          Further reading is attached to each area
                        </span>
                        <span className="mt-0.5 text-xs text-succession-text-secondary leading-snug">
                          On your readiness card, under the area it belongs to.
                        </span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button onClick={() => onComplete?.(result)} className="gap-2">
                See my plan
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

ReadinessAssessmentFlow.propTypes = {
  planSlug: PropTypes.string.isRequired,
  onComplete: PropTypes.func,
  onClose: PropTypes.func,
};

ReadinessAssessmentFlow.defaultProps = {
  onComplete: undefined,
  onClose: undefined,
};

export default ReadinessAssessmentFlow;
